// NotificationBell — Bell icon with unread badge + dropdown popover in CRM header

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import {
  useUnreadNotificationCount,
  useNotifications,
  useMarkNotificationRead,
  useMarkAllRead,
} from '@/hooks/useActivityCenter';
import type { NotificationItem } from '@/types/activityCenter';

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `hace ${days}d`;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: unread } = useUnreadNotificationCount();
  const { data: notifications } = useNotifications({ limit: 5 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllRead();

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const count = unread?.count ?? 0;
  const items = notifications?.items ?? [];

  function handleItemClick(item: NotificationItem) {
    if (!item.is_read) {
      markRead.mutate(item.id);
    }
    setOpen(false);
    if (item.action_url) {
      navigate(item.action_url);
    }
  }

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        className="relative p-2 h-9"
        onClick={() => setOpen(!open)}
        aria-label="Notificaciones"
      >
        <BellIcon className="w-5 h-5 text-gray-600" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Notificaciones
            </h3>
            {count > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="text-xs text-primary hover:underline"
              >
                Marcar todas leidas
              </button>
            )}
          </div>

          {/* Items */}
          <div className="max-h-[320px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No hay notificaciones
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`px-4 py-3 cursor-pointer border-b border-gray-50 dark:border-gray-800 last:border-b-0 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    !item.is_read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!item.is_read && (
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    )}
                    <div className={`flex-1 min-w-0 ${item.is_read ? 'ml-4' : ''}`}>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                        {item.message}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {formatRelativeTime(item.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-2">
            <button
              onClick={() => {
                setOpen(false);
                navigate('/crm/history?tab=notifications');
              }}
              className="text-xs text-primary hover:underline w-full text-center"
            >
              Ver todo el historial
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
